import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Trash2, Plus, Clock, Zap } from "lucide-react";
import { toast } from "sonner";
import MediaSourcePicker from "./MediaSourcePicker";

/**
 * AdSchedule — up to 20 scheduled ad break slots in the Director Console's
 * Monitors column. Each slot holds:
 *   - media (upload or pasted link) via the shared MediaSourcePicker
 *   - label
 *   - cue time as an HH:MM:SS offset from broadcast start (used for planning)
 *
 * Storage: localStorage under `tapin_ad_schedule:{streamId}` so edits survive
 * page reloads. Backend persistence is a future pass.
 *
 * When an ad's cue time is reached (polled every 15s while `bcStatus === "live"`),
 * we toast the host "Ad is due — fire when ready" so they can pull the trigger
 * from the Scenes grid or the Pro Kit.
 */
const MAX_ADS = 20;

export default function AdSchedule({ streamId, bcStatus, startedAt, defaultOpen = false }) {
  const storageKey = `tapin_ad_schedule:${streamId}`;
  const [open, setOpen] = useState(defaultOpen);
  const [ads, setAds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });
  const [picker, setPicker] = useState(null); // { index, initial }
  const [firedAds, setFiredAds] = useState(() => new Set()); // idx → fired in this session

  const persist = (next) => {
    setAds(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
  };

  const addSlot = () => {
    if (ads.length >= MAX_ADS) {
      toast.error(`Max ${MAX_ADS} ad slots per stream`, { id: "ad-cap" });
      return;
    }
    persist([...ads, { id: `ad_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, label: "", cue: "00:05:00", media: null }]);
  };

  const patch = (idx, updates) => {
    persist(ads.map((a, i) => (i === idx ? { ...a, ...updates } : a)));
  };

  const remove = (idx) => {
    persist(ads.filter((_, i) => i !== idx));
  };

  // --- Cue-time notifier: every 15s while live, check if any ad's cue time has
  //     passed AND we haven't fired/toasted it yet this session. ---
  useEffect(() => {
    if (bcStatus !== "live" || !startedAt) return;
    const tick = () => {
      const elapsedSec = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
      ads.forEach((a, i) => {
        const cueSec = parseCueSec(a.cue);
        if (cueSec > 0 && elapsedSec >= cueSec && !firedAds.has(a.id)) {
          setFiredAds((prev) => { const n = new Set(prev); n.add(a.id); return n; });
          const label = a.label || `Ad #${i + 1}`;
          toast.info(`Ad break due: ${label}`, { id: `ad-due-${a.id}`, duration: 8000 });
        }
      });
    };
    tick();
    const h = setInterval(tick, 15000);
    return () => clearInterval(h);
  }, [ads, bcStatus, startedAt, firedAds]);

  const livePreview = useMemo(() => ads.find((a) => a.media) || null, [ads]);

  return (
    <section
      data-testid="monitors-ad-schedule"
      className="mt-1 bg-black/40 border border-tv-accent/25 rounded-lg p-2"
    >
      <button
        data-testid="ad-schedule-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-tv-accent hover:text-yellow-300"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Zap className="w-3 h-3" /> Ad Schedule
        <span className="ml-auto bg-tv-accent/20 text-tv-accent border border-tv-accent/40 text-[9px] font-black px-1.5 py-0.5 rounded-full">
          {ads.length}/{MAX_ADS}
        </span>
      </button>

      {/* Live preview — auto-plays the first attached ad, kept outside the
          collapse so the Monitors column still surfaces the media even when
          the list is collapsed (previous "Ad preview" behaviour). */}
      {livePreview?.media?.url && (
        <div
          data-testid="ad-schedule-preview"
          className="relative aspect-video mt-2 rounded-md overflow-hidden border border-white/10 cursor-pointer"
          onClick={() => {
            const idx = ads.findIndex((a) => a.id === livePreview.id);
            setPicker({ index: idx, initial: livePreview.media });
          }}
          title="Click to change ad media"
        >
          <video
            src={livePreview.media.url}
            muted
            loop
            autoPlay
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span className="absolute top-1 left-1 bg-tv-accent text-black text-[8px] font-black px-1.5 py-0.5 rounded uppercase">AD</span>
          <div className="absolute bottom-1 left-1 right-1 text-[9px] text-white/80 bg-black/60 rounded px-1 py-0.5 truncate">
            {livePreview.label || livePreview.media.label || "Ad clip"}
          </div>
        </div>
      )}

      {open && (
        <div className="mt-2 space-y-1.5">
          {ads.length === 0 ? (
            <div data-testid="ad-schedule-empty" className="text-[10px] text-white/40 italic">
              No ads scheduled. Tap + to add up to {MAX_ADS} ad breaks.
            </div>
          ) : (
            ads.map((a, i) => (
              <div
                key={a.id}
                data-testid={`ad-schedule-row-${i}`}
                className="bg-white/5 border border-white/10 rounded-md p-1.5 space-y-1"
              >
                <div className="flex items-center gap-1">
                  <span className="text-[9px] uppercase tracking-wider font-black text-tv-accent w-6">#{i + 1}</span>
                  <input
                    type="text"
                    data-testid={`ad-schedule-label-${i}`}
                    value={a.label || ""}
                    onChange={(e) => patch(i, { label: e.target.value })}
                    placeholder="Label (e.g. Sponsor 1)"
                    className="flex-1 bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-white outline-none focus:border-tv-accent"
                  />
                  <button
                    data-testid={`ad-schedule-remove-${i}`}
                    onClick={() => remove(i)}
                    className="text-white/40 hover:text-red-400 p-0.5"
                    title="Remove ad slot"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-white/40 shrink-0" />
                  <input
                    type="text"
                    data-testid={`ad-schedule-cue-${i}`}
                    value={a.cue || ""}
                    onChange={(e) => patch(i, { cue: e.target.value })}
                    placeholder="HH:MM:SS"
                    pattern="^[0-9]{1,2}:[0-5][0-9]:[0-5][0-9]$"
                    className="w-20 bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-white outline-none focus:border-tv-accent font-mono"
                  />
                  <button
                    data-testid={`ad-schedule-media-${i}`}
                    onClick={() => setPicker({ index: i, initial: a.media })}
                    className={`flex-1 text-[10px] font-bold py-0.5 rounded border transition ${
                      a.media
                        ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/30 hover:bg-emerald-500/30"
                        : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                    }`}
                    title={a.media?.label || "Attach media"}
                  >
                    {a.media ? "▶ Media set" : "+ Add media"}
                  </button>
                </div>
              </div>
            ))
          )}

          <button
            data-testid="ad-schedule-add"
            onClick={addSlot}
            disabled={ads.length >= MAX_ADS}
            className="w-full flex items-center justify-center gap-1 bg-tv-accent/15 hover:bg-tv-accent/25 text-tv-accent border border-tv-accent/30 text-[10px] font-bold py-1.5 rounded disabled:opacity-40"
          >
            <Plus className="w-3 h-3" /> Add ad slot {ads.length}/{MAX_ADS}
          </button>
        </div>
      )}

      <MediaSourcePicker
        open={picker !== null}
        onClose={() => setPicker(null)}
        title={picker != null ? `Ad #${picker.index + 1} media` : "Ad media"}
        initial={picker?.initial || null}
        onSave={(data) => {
          if (picker?.index != null) patch(picker.index, { media: data });
        }}
        onClear={() => {
          if (picker?.index != null) patch(picker.index, { media: null });
        }}
      />
    </section>
  );
}

function parseCueSec(s) {
  if (!s) return 0;
  const parts = s.split(":").map((x) => parseInt(x, 10) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}
